import { useEffect, useState } from "react";
import api from "../../../../lib/api";
import { errorToastFromAxios } from "../../../../lib/supportToast";
import { Input } from "../../../../components/ui/input";
import { toast } from "sonner";
import { Check, Globe, Boxes as BoxesIcon } from "lucide-react";


// ─────────────────────────────────────────────────────────────────────────
// CatalogRulesEditor
//   Seller-wide catalog governance rules (apply across ALL buyers, not per-
//   partner). Two settings:
//     • stock_hide_threshold — hide SKU from dropship when stock < N.
//     • excluded_regions     — refuse orders to these ISO country / US
//                              state codes. Blocks CSV upload rows +
//                              forward.
//   Persists via GET/PUT /api/dropship/catalog-rules.
// ─────────────────────────────────────────────────────────────────────────
const US_STATE_OPTIONS = [
  ["US-AL","Alabama"],["US-AK","Alaska"],["US-AZ","Arizona"],["US-AR","Arkansas"],
  ["US-CA","California"],["US-CO","Colorado"],["US-CT","Connecticut"],["US-DE","Delaware"],
  ["US-FL","Florida"],["US-GA","Georgia"],["US-HI","Hawaii"],["US-ID","Idaho"],
  ["US-IL","Illinois"],["US-IN","Indiana"],["US-IA","Iowa"],["US-KS","Kansas"],
  ["US-KY","Kentucky"],["US-LA","Louisiana"],["US-ME","Maine"],["US-MD","Maryland"],
  ["US-MA","Massachusetts"],["US-MI","Michigan"],["US-MN","Minnesota"],["US-MS","Mississippi"],
  ["US-MO","Missouri"],["US-MT","Montana"],["US-NE","Nebraska"],["US-NV","Nevada"],
  ["US-NH","New Hampshire"],["US-NJ","New Jersey"],["US-NM","New Mexico"],["US-NY","New York"],
  ["US-NC","North Carolina"],["US-ND","North Dakota"],["US-OH","Ohio"],["US-OK","Oklahoma"],
  ["US-OR","Oregon"],["US-PA","Pennsylvania"],["US-RI","Rhode Island"],["US-SC","South Carolina"],
  ["US-SD","South Dakota"],["US-TN","Tennessee"],["US-TX","Texas"],["US-UT","Utah"],
  ["US-VT","Vermont"],["US-VA","Virginia"],["US-WA","Washington"],["US-WV","West Virginia"],
  ["US-WI","Wisconsin"],["US-WY","Wyoming"],["US-DC","District of Columbia"],
];
const COUNTRY_OPTIONS = [
  ["CA","Canada"],["MX","Mexico"],["GB","United Kingdom"],["AU","Australia"],
  ["DE","Germany"],["FR","France"],["JP","Japan"],["CN","China"],["IN","India"],
  ["BR","Brazil"],["KR","South Korea"],["IT","Italy"],["ES","Spain"],["NL","Netherlands"],
];
export default function CatalogRulesEditor() {
  const [rules, setRules] = useState({ stock_hide_threshold: 0, excluded_regions: [] });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/dropship/catalog-rules")
      .then(r => {
        const d = r.data || {};
        setRules({
          stock_hide_threshold: Number(d.stock_hide_threshold || 0),
          excluded_regions: Array.isArray(d.excluded_regions) ? d.excluded_regions : [],
        });
      })
      .catch(() => { /* keep defaults */ })
      .finally(() => setLoaded(true));
  }, []);

  const patch = (fn) => { setRules(r => fn({ ...r })); setDirty(true); };
  const toggleRegion = (code) => patch(r => {
    const set = new Set(r.excluded_regions);
    if (set.has(code)) set.delete(code); else set.add(code);
    r.excluded_regions = Array.from(set);
    return r;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dropship/catalog-rules", rules);
      setDirty(false);
      toast.success("Catalog rules saved · applied to buyer view immediately");
    } catch (e) {
      errorToastFromAxios(e, "Could not save catalog rules");
    } finally { setSaving(false); }
  };

  if (!loaded) return null;

  const usSelected = rules.excluded_regions.filter(c => c.startsWith("US-"));
  const countrySelected = rules.excluded_regions.filter(c => !c.startsWith("US-"));

  return (
    <div className="px-5 pb-5 pt-2 space-y-4 border-t border-border" data-testid="catalog-rules-editor">
      {/* Stock threshold */}
      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex items-start gap-3">
          <BoxesIcon className="w-4 h-4 shrink-0 text-foreground/60 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium">Auto-hide low-stock SKUs</div>
            <div className="text-[11.5px] text-foreground/60 mt-0.5">
              When a product's stock drops below this threshold, it's automatically hidden from every dropship buyer's view — prevents overselling when inventory dips. Set to <span className="mono">0</span> to disable the gate.
            </div>
          </div>
          <div className="shrink-0 relative">
            <Input
              type="number"
              min="0"
              max="100000"
              step="1"
              value={rules.stock_hide_threshold}
              onChange={e => patch(r => { r.stock_hide_threshold = Number(e.target.value || 0); return r; })}
              data-testid="stock-hide-threshold"
              className="h-10 w-32 pl-3 pr-14 mono text-right"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono text-foreground/55">units</span>
          </div>
        </div>
      </div>

      {/* Regional exclusions */}
      <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="regional-exclusions">
        <div className="flex items-start gap-3">
          <Globe className="w-4 h-4 shrink-0 text-foreground/60 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium">Regional restrictions</div>
            <div className="text-[11.5px] text-foreground/60 mt-0.5">
              SKUs you won't ship to. Orders headed to these regions are rejected at CSV upload + forward with a clear error. Great for hazmat, alcohol, or countries you don't have carriers for.
            </div>
          </div>
          {rules.excluded_regions.length > 0 && (
            <span className="shrink-0 text-[10px] mono uppercase tracking-[0.14em] rounded-full px-2 py-1 bg-secondary text-foreground/70 border border-border">
              {rules.excluded_regions.length} excluded
            </span>
          )}
        </div>

        {/* Non-US countries */}
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 mb-2">Countries</div>
          <div className="flex flex-wrap gap-1.5">
            {COUNTRY_OPTIONS.map(([code, name]) => {
              const on = countrySelected.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleRegion(code)}
                  data-testid={`region-toggle-${code}`}
                  className={`text-[11.5px] rounded-full h-7 px-2.5 border transition-all ${
                    on ? "bg-rose-600 text-white border-rose-600" :
                          "bg-white text-foreground/70 border-border hover:border-foreground/40"
                  }`}
                >
                  {on && <span className="mr-1">×</span>}{name}
                </button>
              );
            })}
          </div>
        </div>

        {/* US states */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">US states</div>
            {usSelected.length > 0 && (
              <button
                type="button"
                onClick={() => patch(r => { r.excluded_regions = r.excluded_regions.filter(c => !c.startsWith("US-")); return r; })}
                data-testid="clear-us-states"
                className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 hover:text-foreground underline underline-offset-2"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {US_STATE_OPTIONS.map(([code, name]) => {
              const on = usSelected.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleRegion(code)}
                  data-testid={`region-toggle-${code}`}
                  className={`text-[11px] rounded-full h-7 px-2.5 border transition-all ${
                    on ? "bg-rose-600 text-white border-rose-600" :
                          "bg-white text-foreground/70 border-border hover:border-foreground/40"
                  }`}
                  title={name}
                >
                  {on && <span className="mr-1">×</span>}{code.replace("US-", "")}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {dirty && (
        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="catalog-rules-save"
            className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60"
          >
            <Check className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save catalog rules"}
          </button>
        </div>
      )}
    </div>
  );
}
